import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, isAfter, isBefore, startOfToday } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { X, HelpCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { logActivity } from '../utils/activityLogger';
import Tooltip from './Tooltip';
import LoadingSpinner from './LoadingSpinner';

interface Property {
  id: string;
  name: string;
  capacity: number;
}

const bookingSchema = z.object({
  property_id: z.string().min(1, 'Champ requis'),
  guest_first_name: z.string().min(1, 'Champ requis'),
  guest_last_name: z.string().min(1, 'Champ requis'),
  guest_count: z.number()
    .min(1, 'Minimum 1 invité')
    .max(50, 'La capacité maximum est de 50 personnes'),
  check_in_date: z.string().min(1, 'Champ requis').refine((date) => {
    return !isBefore(new Date(date), startOfToday());
  }, 'La date d\'arrivée ne peut pas être dans le passé'),
  check_out_date: z.string().min(1, 'Champ requis'),
  cleaning_service: z.boolean().default(false),
  bed_sheets_service: z.boolean().default(false),
  guest_email: z.string().email('Email invalide').optional().or(z.literal('')),
  guest_phone: z.string().optional().or(z.literal('')),
  door_code: z.string().optional().or(z.literal('')),
  comments: z.string().optional().or(z.literal(''))
}).refine((data) => {
  return isAfter(new Date(data.check_out_date), new Date(data.check_in_date));
}, {
  message: "La date de départ doit être après la date d'arrivée",
  path: ['check_out_date']
});

type BookingFormData = z.infer<typeof bookingSchema>;

interface BookingFormProps {
  booking?: any;
  onClose: () => void;
  onSuccess: () => void;
}

const BookingForm = ({ booking, onClose, onSuccess }: BookingFormProps) => {
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const [properties, setProperties] = useState<Property[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPropertyCapacity, setSelectedPropertyCapacity] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setError,
    clearErrors,
    setValue
  } = useForm<BookingFormData>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      cleaning_service: false,
      bed_sheets_service: false
    }
  });

  const checkInDate = watch('check_in_date');
  const minCheckOutDate = checkInDate ? format(new Date(checkInDate), 'yyyy-MM-dd') : '';
  const today = format(new Date(), 'yyyy-MM-dd');
  const selectedPropertyId = watch('property_id');
  const guestCount = watch('guest_count');

  useEffect(() => {
    const initializeForm = async () => {
      setIsLoading(true);
      try {
        // Fetch properties
        const { data: propertiesData } = await supabase
          .from('properties')
          .select('id, name, capacity');
        
        if (propertiesData) {
          setProperties(propertiesData);
        }

        // If editing, initialize form with booking data
        if (booking) {
          // Ensure we have the property_id from either the direct property_id field
          // or from the property object
          const propertyId = booking.property_id || booking.property?.id;
          
          const formData = {
            ...booking,
            property_id: propertyId
          };
          
          reset(formData);

          // Set property capacity
          const property = propertiesData?.find(p => p.id === propertyId);
          if (property) {
            setSelectedPropertyCapacity(property.capacity);
          }
        }
      } catch (error) {
        console.error('Error initializing form:', error);
        addNotification('error', 'Erreur lors du chargement des données');
      } finally {
        setIsLoading(false);
      }
    };

    initializeForm();
  }, [booking, reset, addNotification]);

  useEffect(() => {
    if (selectedPropertyId) {
      const property = properties.find(p => p.id === selectedPropertyId);
      if (property) {
        setSelectedPropertyCapacity(property.capacity);
        if (guestCount > property.capacity) {
          setError('guest_count', {
            type: 'manual',
            message: `Le nombre d'invités ne peut pas dépasser la capacité du logement (${property.capacity} personnes)`
          });
        } else {
          clearErrors('guest_count');
        }
      }
    }
  }, [selectedPropertyId, guestCount, properties, setError, clearErrors]);

  async function onSubmit(data: BookingFormData) {
    try {
      setIsSubmitting(true);
      const userId = (await supabase.auth.getUser()).data.user?.id;

      if (!userId) {
        throw new Error('User not authenticated');
      }

      const bookingData = {
        ...data,
        created_by: userId
      };

      // Si c'est une modification, on s'assure que le property_id n'a pas changé
      if (booking?.id) {
        if (booking.property_id !== data.property_id) {
          throw new Error('Le logement ne peut pas être modifié');
        }
      }

      const { error } = booking?.id
        ? await supabase
            .from('bookings')
            .update(bookingData)
            .eq('id', booking.id)
        : await supabase
            .from('bookings')
            .insert([bookingData]);

      if (error) throw error;

      await logActivity({
        user_id: userId,
        action: booking?.id ? 'update' : 'create',
        entity_type: 'booking',
        entity_id: booking?.id,
        details: bookingData
      });

      addNotification('success', booking?.id 
        ? 'Réservation modifiée' 
        : 'Réservation créée'
      );
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error saving booking:', error);
      addNotification('error', error.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading || isSubmitting) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const selectedProperty = booking?.property || properties.find(p => p.id === selectedPropertyId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 sm:px-6 flex justify-between items-center z-10">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            {booking ? 'Modifier la réservation' : 'Nouvelle réservation'}
            <Tooltip content="Remplissez les informations de la réservation">
              <HelpCircle className="w-5 h-5 text-gray-400" />
            </Tooltip>
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-4 sm:p-6 space-y-4">
          {/* Logement et nombre d'invités */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Logement
              </label>
              {booking ? (
                <div className="w-full p-2 bg-gray-100 rounded-lg border border-gray-300 text-gray-700">
                  {selectedProperty?.name || 'Chargement...'}
                </div>
              ) : (
                <select
                  {...register('property_id')}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">Sélectionner un logement</option>
                  {properties.map((property) => (
                    <option key={property.id} value={property.id}>
                      {property.name} (max {property.capacity} pers.)
                    </option>
                  ))}
                </select>
              )}
              {errors.property_id && (
                <p className="mt-1 text-sm text-red-600">{errors.property_id.message}</p>
              )}
              {booking && (
                <p className="mt-1 text-sm text-gray-500">
                  Le logement ne peut pas être modifié après la création de la réservation
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre d'invités (max {selectedPropertyCapacity} pers.)
              </label>
              <input
                type="number"
                {...register('guest_count', { valueAsNumber: true })}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                min="1"
                max={selectedPropertyCapacity}
              />
              {errors.guest_count && (
                <p className="mt-1 text-sm text-red-600">{errors.guest_count.message}</p>
              )}
            </div>
          </div>

          {/* Informations de l'invité */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prénom de l'invité
              </label>
              <input
                type="text"
                {...register('guest_first_name')}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {errors.guest_first_name && (
                <p className="mt-1 text-sm text-red-600">{errors.guest_first_name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom de l'invité
              </label>
              <input
                type="text"
                {...register('guest_last_name')}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {errors.guest_last_name && (
                <p className="mt-1 text-sm text-red-600">{errors.guest_last_name.message}</p>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date d'arrivée
              </label>
              <input
                type="date"
                {...register('check_in_date')}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                min={today}
              />
              {errors.check_in_date && (
                <p className="mt-1 text-sm text-red-600">{errors.check_in_date.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de départ
              </label>
              <input
                type="date"
                {...register('check_out_date')}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                min={minCheckOutDate}
              />
              {errors.check_out_date && (
                <p className="mt-1 text-sm text-red-600">{errors.check_out_date.message}</p>
              )}
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email de l'invité
              </label>
              <input
                type="email"
                {...register('guest_email')}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {errors.guest_email && (
                <p className="mt-1 text-sm text-red-600">{errors.guest_email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Téléphone de l'invité
              </label>
              <input
                type="tel"
                {...register('guest_phone')}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {errors.guest_phone && (
                <p className="mt-1 text-sm text-red-600">{errors.guest_phone.message}</p>
              )}
            </div>
          </div>

          {/* Code d'accès */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Code d'accès
            </label>
            <input
              type="text"
              {...register('door_code')}
              className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
            {errors.door_code && (
              <p className="mt-1 text-sm text-red-600">{errors.door_code.message}</p>
            )}
          </div>

          {/* Commentaires */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Commentaires
            </label>
            <textarea
              {...register('comments')}
              rows={3}
              className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
            {errors.comments && (
              <p className="mt-1 text-sm text-red-600">{errors.comments.message}</p>
            )}
          </div>

          {/* Services */}
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('cleaning_service')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                Service de ménage
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('bed_sheets_service')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                Service de draps
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3 sm:px-6 -mx-4 sm:-mx-6 mt-6">
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700"
              >
                {isSubmitting ? 'Enregistrement...' : booking ? 'Modifier' : 'Créer'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingForm;